// ============================================================================
// Service Management Types — ServiceCard, InitialEvaluation, CriticalTaskPermit
// ============================================================================

// --- Shared Types ---

export type CriticalTaskType =
  | 'trabajo_altura'
  | 'espacio_confinado'
  | 'trabajo_electrico'
  | 'trabajo_caliente'
  | 'excavacion'
  | 'izaje'
  | 'maquinaria'
  | 'herramientas_alto_riesgo'
  | 'sustancias_quimicas'
  | 'riesgo_incendio'
  | 'agentes_fisicos'
  | 'agentes_biologicos';

export const CRITICAL_TASK_TYPES: { value: CriticalTaskType; label: string }[] = [
  { value: 'trabajo_altura', label: 'Trabajo en Altura' },
  { value: 'espacio_confinado', label: 'Espacio Confinado' },
  { value: 'trabajo_electrico', label: 'Trabajo Eléctrico' },
  { value: 'trabajo_caliente', label: 'Trabajo en Caliente' },
  { value: 'excavacion', label: 'Excavación' },
  { value: 'izaje', label: 'Izaje' },
  { value: 'maquinaria', label: 'Maquinaria' },
  { value: 'herramientas_alto_riesgo', label: 'Herramientas de Alto Riesgo' },
  { value: 'sustancias_quimicas', label: 'Sustancias Químicas' },
  { value: 'riesgo_incendio', label: 'Riesgo de Incendio' },
  { value: 'agentes_fisicos', label: 'Agentes Físicos' },
  { value: 'agentes_biologicos', label: 'Agentes Biológicos' },
];

export interface PopulatedUser {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  cedula?: string;
  fullName?: string;
  // Only present when the caller explicitly asked to populate it (e.g. to
  // resolve whether this person is external to the current company via
  // getExternalCompanyInfo).
  companies?: Array<Record<string, any>>;
}

export interface PopulatedCompany {
  _id: string;
  name: string;
}

// --- ServiceCard (Ficha de Servicio) ---

export type ServiceCardStatus = 'active' | 'evaluating' | 'closed';

export interface EvaluationComment {
  category: string;
  text: string;
  evidence?: string;
  isAnonymous: boolean;
  createdBy: string | PopulatedUser;
  createdAt: string;
}

export interface ServiceEvaluation {
  atencionCliente?: number;
  cumplimientoSeguridad?: number;
  puntualidad?: number;
  capacidadTecnica?: number;
  flexibilidad?: number;
  comments: EvaluationComment[];
  evaluatedBy?: string | PopulatedUser;
  evaluatedAt?: string;
}

export interface ServiceCardAuditEntry {
  action: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  changedBy: string | PopulatedUser;
  changedAt: string;
  ip?: string;
}

export interface ServiceCard {
  _id: string;
  code: string;
  company: string | PopulatedCompany;
  companyContact: string | PopulatedUser;
  contractorContact: string | PopulatedUser;
  title?: string;
  description: string;
  requiresPurchaseOrder: boolean;
  startDate: string;
  endDate: string;
  status: ServiceCardStatus;
  evaluation: ServiceEvaluation;
  // Evaluaciones iniciales vinculadas — solo presente en la respuesta del
  // listado (getServiceCards agrupa una consulta batch para evitar N+1).
  evaluations?: Array<{ _id: string; code: string; status: string }>;
  auditLog: ServiceCardAuditEntry[];
  createdBy: string | PopulatedUser;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceCardCreateData {
  company?: string;
  companyContact: string;
  contractorContact: string;
  title?: string;
  description: string;
  requiresPurchaseOrder?: boolean;
  startDate: string;
  endDate: string;
}

export interface ServiceCardUpdateData {
  companyContact?: string;
  contractorContact?: string;
  title?: string;
  description?: string;
  requiresPurchaseOrder?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface ServiceCardFilters {
  page?: number;
  limit?: number;
  status?: ServiceCardStatus;
  /** Comma-separated statuses to exclude; ignored by the BE when `status` is also set. */
  excludeStatus?: string;
  company?: string;
  search?: string;
}

export interface ServiceCardEvaluateData {
  atencionCliente: number;
  cumplimientoSeguridad: number;
  puntualidad: number;
  capacidadTecnica: number;
  flexibilidad: number;
  comments?: Array<{
    category: string;
    text: string;
    isAnonymous?: boolean;
  }>;
}

export interface PaginatedServiceCards {
  data: ServiceCard[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ServiceCardStats {
  byStatus: {
    active: number;
    evaluating: number;
    closed: number;
  };
  total: number;
}

// --- InitialEvaluation (Evaluación Inicial) ---

export type InitialEvaluationStatus = 'draft' | 'pending_approval' | 'approved' | 'partial' | 'rejected' | 'closed';

export interface EvaluationWorker {
  workerId: string | PopulatedUser;
  role: string;
}

export interface ResolutionDocument {
  url: string;
  description?: string;
  uploadedBy: string | PopulatedUser;
  uploadedAt: string;
}

export interface InitialEvaluation {
  _id: string;
  code: string;
  serviceCard: string | ServiceCard;
  company: string | PopulatedCompany;
  companyContact: string | PopulatedUser;
  contractorContact: string | PopulatedUser;
  title?: string;
  description: string;
  location: string;
  startDate: string;
  endDate: string;
  workers: EvaluationWorker[];
  equipment: string[];
  chemicals: string[];
  ppe: string[];
  generalRisks: string[];
  criticalTasks: CriticalTaskType[];
  hasFines: boolean;
  fineDetails?: string;
  approvalChain: import('./index').ApprovalChainEntry[];
  status: InitialEvaluationStatus;
  partialNotes?: string;
  resolutionDocuments: ResolutionDocument[];
  closedBy?: string | PopulatedUser;
  closedAt?: string;
  createdBy: string | PopulatedUser;
  createdAt: string;
  updatedAt: string;
}

export interface InitialEvaluationCreateData {
  /** Optional as of 2026-06 (standalone evaluations). When absent,
   *  `company` must be supplied instead. */
  serviceCard?: string;
  company?: string;
  companyContact?: string;
  contractorContact?: string;
  title?: string;
  description: string;
  location: string;
  startDate: string;
  endDate: string;
  workers: Array<{ workerId: string; role: string }>;
  equipment: string[];
  chemicals: string[];
  ppe: string[];
  generalRisks: string[];
  criticalTasks: CriticalTaskType[];
  hasFines: boolean;
  fineDetails?: string;
  approvalChain: Array<{ approverId: string; isRequired: boolean; isBench: boolean }>;
}

export interface InitialEvaluationUpdateData extends Partial<InitialEvaluationCreateData> {}

export interface InitialEvaluationFilters {
  page?: number;
  limit?: number;
  serviceCard?: string;
  company?: string;
  status?: InitialEvaluationStatus;
  search?: string;
}

export interface PaginatedInitialEvaluations {
  data: InitialEvaluation[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface InitialEvaluationStats {
  byStatus: Record<InitialEvaluationStatus, number>;
  total: number;
}

// --- CriticalTaskPermit (Permiso de Trabajo) ---

export type CriticalTaskPermitStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'partial'
  | 'rejected'
  | 'in_progress'
  | 'suspended'
  | 'completed'
  | 'closed'
  | 'expirado';

export interface AssignedWorker {
  workerId: string | PopulatedUser;
  role: 'principal' | 'support' | 'observer';
  status: 'authorized' | 'blocked';
  blockReason?: string;
  requiredCourses: string[];
}

export interface ChecklistItem {
  _id?: string;
  id?: string;
  name: string;
  description?: string;
  isChecked?: boolean;
  checkedBy?: string | PopulatedUser;
  checkedAt?: string;
  evidenceImageUrl?: string;
  imageUrl?: string;
  imageDownloadUrl?: string;
}

export interface ChecklistSection {
  _id?: string;
  id?: string;
  name: string;
  description?: string;
  isMandatory: boolean;
  items: ChecklistItem[];
}

export interface RequiredCourse {
  courseId: string;
  courseName: string;
}

export interface EquipmentItem {
  _id?: string;
  name: string;
  description?: string;
  imageUrl?: string;
  imageDownloadUrl?: string;
}

export interface CriticalTaskPermit {
  _id: string;
  code: string;
  // Opcional desde 2026-06: los permisos independientes no tienen una
  // cadena de ficha/evaluación padre (ver ICriticalTaskPermit en el BE).
  initialEvaluation?: string | InitialEvaluation;
  serviceCard?: string | ServiceCard;
  company: string | PopulatedCompany;
  companyContact: string | PopulatedUser;
  // Opcional cuando el permiso es independiente (aún sin contraparte proveedor).
  contractorContact?: string | PopulatedUser;
  // Empresa proveedora contraparte — solo presente cuando el permiso tiene
  // un contratista asociado.
  providerCompany?: string | PopulatedCompany;
  criticalTaskTypes: CriticalTaskType[];
  title?: string;
  description: string;
  location: string;
  startDate: string;
  startTime?: string;
  endDate: string;
  endTime?: string;
  workers: AssignedWorker[];
  equipment: EquipmentItem[];
  chemicals: string[];
  ppe: string[];
  generalRisks: string[];
  specificRisks: string[];
  sections: ChecklistSection[];
  requiredCourses: RequiredCourse[];
  approvalChain: import('./index').ApprovalChainEntry[];
  status: CriticalTaskPermitStatus;
  actualStartDate?: string;
  duplicatedFrom?: string;
  createdBy: string | PopulatedUser;
  createdAt: string;
  updatedAt: string;
}

export interface CriticalTaskPermitCreateData {
  /** Optional as of 2026-06 (standalone permits). When absent,
   *  `company` must be supplied instead. */
  initialEvaluation?: string;
  company?: string;
  companyContact?: string;
  contractorContact?: string;
  criticalTaskTypes: CriticalTaskType[];
  title?: string;
  description: string;
  location: string;
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  workers: Array<{
    workerId: string;
    role: 'principal' | 'support' | 'observer';
    requiredCourses?: string[];
  }>;
  equipment: Array<{ name: string; description?: string; imageUrl?: string }>;
  chemicals: string[];
  ppe: string[];
  generalRisks: string[];
  specificRisks: string[];
  sections: ChecklistSection[];
  approvalChain: Array<{ approverId: string; isRequired: boolean; isBench: boolean }>;
}

export interface CriticalTaskPermitUpdateData extends Partial<CriticalTaskPermitCreateData> {}

export interface CriticalTaskPermitFilters {
  page?: number;
  limit?: number;
  status?: CriticalTaskPermitStatus;
  initialEvaluation?: string;
  serviceCard?: string;
  company?: string;
  criticalTaskType?: CriticalTaskType;
  search?: string;
  assignedToUser?: string;
}

export interface PaginatedCriticalTaskPermits {
  data: CriticalTaskPermit[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CriticalTaskPermitStats {
  byStatus: Record<CriticalTaskPermitStatus, number>;
  total: number;
  alerts?: {
    expiringSoon: number;
    pendingApprovals: number;
  };
}

// --- API Response wrapper ---

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}
